import { useUpdateAiAgentMainSetting } from '@/context/services/ai_agents_setting';
import { useGetAiAgentsSettingService } from '@/context/services/ai_agents_setting/AiAgentsSetting.service';
import { handlePermissionError } from '@/context/services/ai_agents_setting/utils';

import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useState } from 'react';

type UseOfficeSettingActionsProps = {
  onClose: () => void;
};

export const useOfficeSettingActions = ({
  onClose,
}: UseOfficeSettingActionsProps) => {
  const { mutateAsync: updateSetting } = useUpdateAiAgentMainSetting();
  const { refetch } = useGetAiAgentsSettingService();
  const { t } = useTranslation();
  const notify = useToastAPI();
  const [isEnablePending, setIsEnablePending] = useState(false);
  const [isDisablePending, setIsDisablePending] = useState(false);

  const handleConfirmEnable = useCallback(async () => {
    try {
      setIsEnablePending(true);
      const result = await updateSetting(true);
      if (result.status === 'success') {
        notify.success({
          title: t('ai_agents_setting_main_setting_use_start_success'),
          duration: 5,
        });
        onClose();
        return;
      } else {
        // Handle errors: refetch first, reload only if refetch also fails with 401/403
        const shouldReload = await handlePermissionError(
          result.statusCode,
          refetch
        );
        if (shouldReload) {
          return;
        }
        // Show API error message if available, otherwise show default error
        const errorMessage =
          result.status === 'error' && result.errorMessages?.length
            ? result.errorMessages.join(', ')
            : t('ai_agents_setting_main_setting_use_start_error');
        notify.error({
          title: errorMessage,
          duration: 5,
        });
      }
    } catch (error) {
      notify.error({
        title: t('system_error'),
        duration: 5,
      });
    } finally {
      setIsEnablePending(false);
    }
  }, [updateSetting, refetch, notify, onClose, t]);

  const handleConfirmDisable = useCallback(async () => {
    try {
      setIsDisablePending(true);
      const result = await updateSetting(false);
      if (result.status === 'success') {
        notify.success({
          title: t('ai_agents_setting_main_setting_use_stop_success'),
          duration: 5,
        });
        onClose();
        return;
      }
      // Handle errors: refetch first, reload only if refetch also fails with 401/403
      if (result.status === 'error') {
        const shouldReload = await handlePermissionError(
          result.statusCode,
          refetch
        );
        if (shouldReload) {
          return;
        }
      }
      // Show API error message if available, otherwise show default error
      const errorMessage =
        result.status === 'error' && result.errorMessages?.length
          ? result.errorMessages.join(', ')
          : t('ai_agents_setting_main_setting_use_stop_error');
      notify.error({
        title: errorMessage,
        duration: 5,
      });
    } catch (error) {
      notify.error({
        title: t('system_error'),
        duration: 5,
      });
    } finally {
      setIsDisablePending(false);
    }
  }, [updateSetting, refetch, notify, onClose, t]);

  return {
    handleConfirmEnable,
    handleConfirmDisable,
    isEnablePending,
    isDisablePending,
  };
};
